import { useEffect, useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { Button } from '../../../../../../components/button/button.js'
import { Select } from '../../../../../../components/forms/select/select.js'
import { TextArea } from '../../../../../../components/forms/text-area/text-area.js'
import { TextField } from '../../../../../../components/forms/text-field/text-field.js'
import { BaseModal } from '../../../../../../components/modal/base-modal/base-modal.js'
import { formatLedgerDate, todayStr } from '../../../../utils/format.js'
import {
    fetchLeaveBalanceAsOf,
    type LeaveBalanceAsOf,
    saveLeaveBalanceAdjustment,
} from '../../../../utils/portal-api.js'

interface AdjustablePolicy {
    policyId: number
    policyName: string
    leaveTypeName: string
}

interface TimeAwayAdjustModalProps {
    isOpen: boolean
    onClose: () => void
    employeeId: number
    /** Limited leave types only (accrual_method ≠ none) — the ones with a balance. */
    policies: AdjustablePolicy[]
    lang: Language
    /** Called after a successful save so the profile refetches its balances. */
    onSaved: () => void
}

const round1 = (n: number): number => Math.round(n * 10) / 10

// TASK-567: admin balance correction "as of a date" — a signed day amount that
// counts in full from the chosen date, with a mandatory reason. The live
// preview shows Hub's own balance on that date and what it becomes, so an
// accountant's figure ("30.06 jääk 27") can be matched without arithmetic.
export const TimeAwayAdjustModal = ({
    isOpen,
    onClose,
    employeeId,
    policies,
    lang,
    onSaved,
}: TimeAwayAdjustModalProps) => {
    const tM = t.portal.manualAdjustment

    const [policyId, setPolicyId] = useState<number | ''>('')
    const [effectiveDate, setEffectiveDate] = useState('')
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')
    const [preview, setPreview] = useState<LeaveBalanceAsOf | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Fresh form on every open; a single limited type is preselected.
    useEffect(() => {
        if (!isOpen) return
        setPolicyId(policies.length === 1 ? policies[0].policyId : '')
        setEffectiveDate(todayStr())
        setAmount('')
        setNote('')
        setPreview(null)
        setError(null)
    }, [isOpen, policies])

    // Preview Hub's balance as of the chosen date for the chosen type.
    useEffect(() => {
        if (!isOpen || policyId === '' || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
            setPreview(null)
            return
        }
        let cancelled = false
        setPreviewLoading(true)
        void (async () => {
            const result = await fetchLeaveBalanceAsOf(employeeId, policyId, effectiveDate)
            if (cancelled) return
            setPreview(result.ok ? result.data : null)
            setError(result.ok ? null : result.error)
            setPreviewLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [isOpen, employeeId, policyId, effectiveDate])

    const parsedAmount = Number(amount.replace(',', '.'))
    const amountValid = amount.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount !== 0
    const canSave =
        !saving && policyId !== '' && effectiveDate !== '' && amountValid && note.trim() !== ''

    const handleSave = async () => {
        if (policyId === '' || !amountValid) return
        setSaving(true)
        setError(null)
        try {
            const result = await saveLeaveBalanceAdjustment(employeeId, {
                policy_id: policyId,
                amount: parsedAmount,
                effective_date: effectiveDate,
                note: note.trim(),
            })
            if (!result.ok) {
                setError(result.error)
                return
            }
            onClose()
            onSaved()
        } finally {
            setSaving(false)
        }
    }

    const day = (n: number) => t.portal.timeOff.view.dayCount(round1(n))(lang)
    // "−7.9 päeva → 8 päeva" once an amount is typed, the bare figure before.
    const shift = (from: number) =>
        amountValid ? `${day(from)} → ${day(from + parsedAmount)}` : day(from)

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            disableBackdropClose={saving}
            className="portal-book-time-away-modal"
            showClose
            closeAriaLabel={t.portal.timeOff.close(lang)}
        >
            <div className="portal-book-time-away-modal__header">
                <h3 className="portal-book-time-away-modal__title">{tM.title(lang)}</h3>
            </div>

            {policies.length === 0 ? (
                <div className="portal-book-time-away-modal__self-overlap">
                    {tM.noLimitedPolicies(lang)}
                </div>
            ) : (
                <>
                    <div className="portal-book-time-away-modal__form-group">
                        <label className="portal-book-time-away-modal__form-label">
                            {tM.selectPolicy(lang)}
                        </label>
                        <Select
                            className="portal-book-time-away-modal__form-select"
                            placeholder={tM.selectPolicyPlaceholder(lang)}
                            value={policyId === '' ? '' : String(policyId)}
                            onChange={(value) => setPolicyId(value ? Number(value) : '')}
                            options={policies.map((p) => ({
                                value: String(p.policyId),
                                label: `${p.leaveTypeName} — ${p.policyName}`,
                            }))}
                            isDisabled={saving}
                        />
                    </div>

                    <div className="portal-book-time-away-modal__form-group">
                        <TextField
                            label={tM.effectiveDate(lang)}
                            value={effectiveDate}
                            onChange={setEffectiveDate}
                            inputProps={{ type: 'date', max: todayStr() }}
                            note={{ text: tM.effectiveDateHint(lang) }}
                            isDisabled={saving}
                        />
                    </div>

                    <div className="portal-book-time-away-modal__form-group">
                        <TextField
                            label={tM.amount(lang)}
                            value={amount}
                            onChange={setAmount}
                            placeholder="+2.5"
                            inputProps={{ inputMode: 'decimal' }}
                            note={{ text: tM.amountHint(lang) }}
                            isDisabled={saving}
                        />
                    </div>

                    {policyId !== '' && (
                        <div className="time-away__adjust-preview">
                            {preview?.availableUntilYearEnd !== undefined && (
                                <>
                                    <span className="time-away__adjust-preview-label">
                                        {t.portal.timeOff.view.availableUntil(
                                            Number(todayStr().slice(0, 4)),
                                        )(lang)}
                                    </span>
                                    <span className="time-away__adjust-preview-value">
                                        {shift(preview.availableUntilYearEnd)}
                                    </span>
                                </>
                            )}
                            <span className="time-away__adjust-preview-label">
                                {tM.preview(formatLedgerDate(effectiveDate, lang))(lang)}
                            </span>
                            <span className="time-away__adjust-preview-value">
                                {previewLoading || !preview
                                    ? tM.previewLoading(lang)
                                    : shift(preview.balance)}
                            </span>
                            {preview && (
                                <span className="time-away__adjust-preview-hint">
                                    {preview.kind === 'earned'
                                        ? tM.previewEarned(lang)
                                        : tM.previewAvailable(lang)}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="portal-book-time-away-modal__form-group">
                        <TextArea
                            label={tM.note(lang)}
                            value={note}
                            onChange={setNote}
                            placeholder={tM.notePlaceholder(lang)}
                            note={{ text: tM.noteHint(lang) }}
                            isRequired
                            isDisabled={saving}
                        />
                    </div>

                    {error && (
                        <div className="portal-book-time-away-modal__self-overlap">{error}</div>
                    )}

                    <Button
                        className="portal-book-time-away-modal__submit"
                        size="medium"
                        variant="primary"
                        text={saving ? tM.submitting(lang) : tM.submit(lang)}
                        isDisabled={!canSave}
                        onClick={() => void handleSave()}
                    />
                </>
            )}
        </BaseModal>
    )
}
